/* The skills hub's small pieces, shared by its views: who made a skill, a command to
   copy, the loading state and a file's problems. The audit and source badges are
   `shared/components/skill-atoms.tsx`: an agent's Settings shows them too. */
import React, { useState } from "react";
import { Check, Copy } from "lucide-react";

import { personName } from "@/shared/api/members";
import { useSettings } from "@/shared/api/settings";

import type { PersonRef, SkillOwner, SkillProblem } from "@/shared/types";

/** A person as a skill credits them: "you" for the reader. */
export function useCredit() {
  const { data: settings } = useSettings();
  return (person: PersonRef | null) => (!person ? "someone" : person.id === settings?.user.id ? "you" : personName(person));
}

/** Where a workspace's own skill came from, in one line: who made it. */
export function Byline({ skill, className = "" }: { skill: { created_by: PersonRef | null }; className?: string }) {
  const credit = useCredit();
  return <span className={className}>Workspace · by {credit(skill.created_by)}</span>;
}

/** What the checker said about one file, under its row or field. */
export function ProblemLines({ problems, withPath = false }: {
  problems: SkillProblem[];
  /** Name the file each is about: the list stands apart from the files. */
  withPath?: boolean;
}) {
  if (!problems.length) return null;
  return (
    <ul className="m-0 p-0 list-none space-y-0.5">
      {problems.map((p, i) => (
        // two files can share one issue's words, so the place in the list keys it
        <li key={`${i}:${p.path}`} className="text-[12px] leading-[1.5] text-[var(--danger)] [overflow-wrap:anywhere]">
          {withPath && p.path && <span className="font-mono">{p.path}: </span>}
          {p.issue}
        </li>
      ))}
    </ul>
  );
}

/** The GitHub account's avatar; its initials when there is none or it will not load. */
export function OwnerAvatar({ owner, size = 36, rounded = 10 }: { owner: SkillOwner; size?: number; rounded?: number }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, borderRadius: rounded };
  if (owner.avatar_url && !broken) {
    const sized = `${owner.avatar_url}${owner.avatar_url.includes("?") ? "&" : "?"}s=${size * 2}`;
    return (
      <img
        src={sized}
        alt=""
        loading="lazy"
        onError={() => setBroken(true)}
        className="shrink-0 object-cover bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)]"
        style={style}
      />
    );
  }
  return (
    <div
      className="shrink-0 bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center font-semibold uppercase text-[var(--text-secondary)]"
      style={{ ...style, fontSize: Math.round(size * 0.36) }}
    >
      {owner.login.slice(0, 2)}
    </div>
  );
}

export function CopyableCommand({ cmd, className = "" }: { cmd: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={`flex items-center gap-2 min-w-0 h-9 pl-3 pr-1.5 rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] font-mono text-[12px] ${className}`}>
      <span className="text-[var(--text-quaternary)] select-none">$</span>
      <span className="flex-1 min-w-0 truncate text-[var(--text-secondary)]" title={cmd}>{cmd}</span>
      <button
        onClick={copy}
        title="Copy command"
        aria-label="Copy command"
        className="press shrink-0 w-6 h-6 flex items-center justify-center rounded-[6px] text-[var(--text-tertiary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
      >
        {copied ? <Check className="w-3 h-3 text-[var(--success)]" strokeWidth={2} /> : <Copy className="w-3 h-3" strokeWidth={1.75} />}
      </button>
    </div>
  );
}

/** Three bouncing dots — the design system's loading state. */
export function LoadingDots({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="flex items-center justify-center gap-1 py-16 text-[var(--text-tertiary)]">
      <span className="dot-1 w-1.5 h-1.5 rounded-full bg-current" />
      <span className="dot-2 w-1.5 h-1.5 rounded-full bg-current" />
      <span className="dot-3 w-1.5 h-1.5 rounded-full bg-current" />
    </div>
  );
}
